import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bookmark, BookmarkCheck, Trash2 } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { JOURNAL } from "@/constants/testIds";
import { toast } from "sonner";

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [count, setCount] = useState(0);
  const [cap, setCap] = useState(10);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const params = f === "bookmarked" ? { bookmarked: true } : {};
      const { data } = await api.get("/journal/entries", { params });
      setEntries(data.entries);
      setCount(data.count);
      setCap(data.cap);
    } catch (e) {
      toast.error("Could not load your journal.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load("all");
  }, []);

  const toggleBookmark = async (entry, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/journal/entries/${entry.entry_id}/bookmark`);
      setEntries((cur) =>
        cur.map((x) => (x.entry_id === entry.entry_id ? { ...x, bookmarked: data.bookmarked } : x))
      );
    } catch {
      toast.error("Could not bookmark.");
    }
  };

  const del = async (entry, e) => {
    e.stopPropagation();
    if (!window.confirm("Release this entry from your journal?")) return;
    try {
      await api.delete(`/journal/entries/${entry.entry_id}`);
      setEntries((cur) => cur.filter((x) => x.entry_id !== entry.entry_id));
      setCount((c) => c - 1);
      toast.success("Released.");
    } catch {
      toast.error("Could not release.");
    }
  };

  return (
    <div
      data-testid={JOURNAL.page}
      className="relative min-h-screen bg-[#080808] px-6 sm:px-12 lg:px-20 py-12 lg:py-20 pb-32 lg:pb-20"
    >
      <div className="starfield-subtle" />
      <div className="relative max-w-5xl mx-auto">
        <div className="mb-14">
          <div className="font-overline mb-4">Journal</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#F4EFE6] font-light leading-tight">
            Your readings,
            <br />
            kept.
          </h1>
          <div className="mt-6 font-overline text-[#F4EFE6]/50">
            {count} / {cap} entries · free tier
          </div>
        </div>

        <div className="mb-10 flex gap-6 items-center border-b border-[#C8A96E]/15 pb-4">
          <button
            data-testid={JOURNAL.filterAll}
            onClick={() => {
              setFilter("all");
              load("all");
            }}
            className={`font-overline transition-colors ${
              filter === "all" ? "text-[#C8A96E]" : "text-[#F4EFE6]/50 hover:text-[#F4EFE6]"
            }`}
          >
            All
          </button>
          <button
            data-testid={JOURNAL.filterBookmarked}
            onClick={() => {
              setFilter("bookmarked");
              load("bookmarked");
            }}
            className={`font-overline transition-colors ${
              filter === "bookmarked" ? "text-[#C8A96E]" : "text-[#F4EFE6]/50 hover:text-[#F4EFE6]"
            }`}
          >
            Bookmarked
          </button>
        </div>

        {loading && <div className="font-heading text-lg text-[#F4EFE6]/60 breathe">Turning the pages…</div>}
        {!loading && entries.length === 0 && (
          <div className="text-center py-24">
            <div className="font-heading text-3xl text-[#F4EFE6]/70 italic mb-4">Nothing written yet.</div>
            <div className="font-heading text-lg text-[#F4EFE6]/50">Save a reading from the Oracle to begin.</div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {entries.map((e, i) => (
            <motion.button
              key={e.entry_id}
              data-testid={JOURNAL.entry(e.entry_id)}
              onClick={() => setSelected(e)}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="text-left card-frame surface-grain p-6 relative group"
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="font-overline text-[#C8A96E]/80">{e.kind.replace("_", " ")}</div>
                <div className="flex items-center gap-2">
                  <span
                    data-testid={JOURNAL.bookmarkBtn(e.entry_id)}
                    onClick={(ev) => toggleBookmark(e, ev)}
                    className="cursor-pointer p-1 text-[#F4EFE6]/40 hover:text-[#C8A96E] transition-colors"
                  >
                    {e.bookmarked ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
                  </span>
                  <span
                    data-testid={JOURNAL.deleteBtn(e.entry_id)}
                    onClick={(ev) => del(e, ev)}
                    className="cursor-pointer p-1 text-[#F4EFE6]/40 hover:text-[#E9967A] transition-colors"
                  >
                    <Trash2 size={16} />
                  </span>
                </div>
              </div>
              <div className="font-heading italic text-2xl text-[#F4EFE6] mb-2">{e.title}</div>
              <div className="font-overline text-[#F4EFE6]/40">
                {e.date}
                {e.moon_phase ? ` · ${e.moon_phase}` : ""}
              </div>
              {e.narrative?.blessing && (
                <p className="mt-4 font-heading italic text-[#C8A96E]/90 line-clamp-2">
                  {e.narrative.blessing}
                </p>
              )}
            </motion.button>
          ))}
        </div>

        <AnimatePresence>
          {selected && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-50 bg-[#080808]/85 backdrop-blur-md flex items-start justify-center p-4 sm:p-10 overflow-y-auto"
              onClick={() => setSelected(null)}
            >
              <motion.div
                initial={{ y: 20 }}
                animate={{ y: 0 }}
                className="card-frame max-w-2xl w-full p-8 sm:p-12"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="font-overline mb-3">
                  {selected.date} {selected.moon_phase ? `· ${selected.moon_phase}` : ""}
                </div>
                <h2 className="font-heading italic text-3xl text-[#F4EFE6] mb-6">{selected.title}</h2>

                {selected.cards?.length > 0 && (
                  <div className="flex gap-3 mb-6 flex-wrap">
                    {selected.cards.slice(0, 4).map((c, i) => (
                      <div key={i} className="w-16 aspect-square card-frame overflow-hidden rounded-md">
                        {c.image_url && (
                          <img
                            src={mediaUrl(c.image_url)}
                            alt={c.title}
                            className="w-full h-full object-cover"
                            style={{ transform: c.upright ? "none" : "rotate(180deg)" }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {selected.narrative?.signal && (
                  <>
                    <div className="font-overline mb-1">Signal</div>
                    <p className="mb-4 font-heading text-lg text-[#F4EFE6]/90">{selected.narrative.signal}</p>
                    <div className="font-overline mb-1">Movement</div>
                    <p className="mb-4 text-[#F4EFE6]/75">{selected.narrative.movement}</p>
                    <div className="font-overline mb-1">Blessing</div>
                    <p className="mb-2 font-heading italic text-xl text-[#C8A96E]">
                      {selected.narrative.blessing}
                    </p>
                  </>
                )}

                {selected.narrative?.thread && (
                  <>
                    <div className="font-overline mb-1">The Thread</div>
                    <p className="mb-4 font-heading text-lg text-[#F4EFE6]/90">{selected.narrative.thread}</p>
                  </>
                )}

                {selected.narrative?.body && (
                  <p className="font-heading text-lg text-[#F4EFE6]/90">{selected.narrative.body}</p>
                )}

                <div className="mt-8 flex justify-end">
                  <button className="btn-sacred" onClick={() => setSelected(null)}>
                    Close
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
